import { Component, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { markFormGroupTouched, uniqueValueValidator } from '@core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { PreferenceService } from '@core-lib/services/preference.service';
import { IEditPreferences, INotificationPreferenceForm, IPreference, PreferencesNotifications } from '@core-lib/models/preference.model';
import { Preference } from '@core-lib/enums/preference.enum';

@Component({
    selector: 'settings-notifications',
    templateUrl: './settings-notifications.component.html',
    styleUrls: ['./settings-notifications.component.scss'],
    standalone: false
})
export class SettingsNotificationsComponent implements OnInit {
	@Input() structureId: string;
	@Input() allowEdit: boolean;
	@Input() preferencesNotifications$: Observable<PreferencesNotifications> = new Observable<PreferencesNotifications>();

	preference = Preference;
	preferencesNotifications: PreferencesNotifications;
	form!: FormGroup<INotificationPreferenceForm>;
	isEmail1Required = false;
	anyEditMode$: Observable<boolean> = this.preferenceService.anyEditMode$;

	notificationsWithoutValidatorsAndValue: Preference[] = [
		Preference.notifications.operationsRenvoyesCarpa.code,
		Preference.notifications.OppositionChequesEmis.code
	];

	get isEditMode() {
		return this.preferenceService.notificationsEditMode$.value;
	}

	constructor(
		public preferenceService: PreferenceService,
		private toastr: ToastrService,
		private translate: TranslateService
	) {}

	ngOnInit(): void {
		this.preferencesNotifications$.subscribe(preferencesNotifications => {
			this.preferencesNotifications = preferencesNotifications;
			this.initForm();
		});
	}

	private initForm(): void {
		this.preferenceService.notificationsEditMode$.next(false);

		const preferencesControls = this.preferencesNotifications.toList().map(item => {
			const valeurValidators = [];

			if (item.actif && !this.notificationsWithoutValidatorsAndValue.includes(item.code)) {
				valeurValidators.push(Validators.required);
			}

			const group = new FormGroup({
				code: new FormControl(item.code, { nonNullable: true }),
				label: new FormControl(item.label, { nonNullable: true }),
				actif: new FormControl(item.actif, { nonNullable: true }),
				valeur: new FormControl(item.valeur, {
					nonNullable: !this.notificationsWithoutValidatorsAndValue.includes(item.code),
					validators: valeurValidators
				})
			});

			if (!item.actif) {
				group.get('valeur')?.disable();
			}

			group.get('actif')?.valueChanges.subscribe(isActive => {
				const montantCtrl = group.get('valeur');
				if (isActive) {
					if (!this.notificationsWithoutValidatorsAndValue.includes(item.code)) {
						montantCtrl?.addValidators(Validators.required);
					}
					montantCtrl?.enable();
				} else {
					montantCtrl?.removeValidators(Validators.required);
					montantCtrl?.setValue(null);
					montantCtrl?.disable();
				}
				montantCtrl?.updateValueAndValidity();
				this.updateEmailRequiredState();
			});

			return group;
		});

		this.form = new FormGroup<INotificationPreferenceForm>({
			preferences: new FormArray(preferencesControls),
			email1: new FormControl(this.preferencesNotifications.email1.valeur, [
				Validators.email,
				uniqueValueValidator(['email2', 'email3'])
			]),
			email2: new FormControl(this.preferencesNotifications.email2.valeur, [
				Validators.email,
				uniqueValueValidator(['email1', 'email3'])
			]),
			email3: new FormControl(this.preferencesNotifications.email3.valeur, [
				Validators.email,
				uniqueValueValidator(['email1', 'email2'])
			])
		});

		this.updateEmailRequiredState();
	}

	private updateEmailRequiredState(): void {
		const email1Ctrl = this.form.get('email1') as FormControl;
		const preferencesArray = this.form.get('preferences') as FormArray;

		this.isEmail1Required = preferencesArray.controls.some(ctrl => ctrl.get('actif')?.value === true);

		if (this.isEmail1Required) {
			email1Ctrl.addValidators(Validators.required);
		} else {
			email1Ctrl.removeValidators(Validators.required);
		}

		email1Ctrl.updateValueAndValidity();
	}

	edit(): void {
		if (!this.allowEdit) return;
		this.preferenceService.notificationsEditMode$.next(true);
	}

	cancel(): void {
		this.initForm();
	}

	confirm(): void {
		if (!this.allowEdit) return;

		if (!this.form.valid) {
			markFormGroupTouched(this.form);
			return;
		}

		var deactivablePreferences = this.form.value.preferences
			.filter(
				p =>
					p.code == Preference.notifications.virementsCredites.code ||
					p.code == Preference.notifications.operationsRenvoyesCarpa.code
			)
			.map(
				p =>
					<IPreference>{
						actif: p.actif,
						code: p.code,
						valeur: p.valeur
					}
			);

		var preferences = this.form.value.preferences
			.filter(p => p.actif && !deactivablePreferences.some(pref => pref.code === p.code))
			.map(
				p =>
					<IPreference>{
						actif: p.actif,
						code: p.code,
						valeur: p.valeur
					}
			)
			.concat([
				<IPreference>{
					actif: true,
					code: Preference.notifications.email1.code,
					valeur: this.form.value.email1
				},
				<IPreference>{
					actif: true,
					code: Preference.notifications.email2.code,
					valeur: this.form.value.email2
				},
				<IPreference>{
					actif: true,
					code: Preference.notifications.email3.code,
					valeur: this.form.value.email3
				}
			]);

		preferences = preferences.concat(deactivablePreferences);

		const editPreferences: IEditPreferences = {
			category: Preference.category.notifications,
			preferences: preferences
		};

		this.preferenceService.edit(this.structureId, editPreferences).subscribe({
			next: () => {
				this.translate.get('Success.Save').subscribe(msg => this.toastr.success(msg));
				this.preferenceService.notificationsEditMode$.next(false);
				this.preferenceService.get(this.structureId, true);
			},
			error: error => {
				this.toastr.error(error);
			}
		});
	}
}
